import { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Reusable skeleton fallback rendered while a lazy-loaded route chunk is
 * being fetched. Sized to feel like a typical page layout (header bar +
 * a few rows of content) so the screen doesn't collapse to a tiny spinner.
 */
export function PageSkeleton() {
  return (
    <div
      className="flex h-screen w-full items-center justify-center bg-background p-8"
      data-testid="page-skeleton"
    >
      <div className="w-full max-w-3xl space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-64 w-full rounded-xl" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      </div>
    </div>
  );
}

/**
 * Suspense fallback that stays invisible for the first `delayMs` and only
 * then reveals <PageSkeleton />. Most route chunks (especially after a
 * hover- or idle-prefetch) resolve in well under 150ms, so users see a
 * direct page-to-page transition with no skeleton flash. Slow loads
 * (cold cache, slow network) still get the skeleton so the screen never
 * looks frozen.
 */
export function DelayedPageSkeleton({ delayMs = 150 }: { delayMs?: number }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setShow(true), delayMs);
    return () => window.clearTimeout(id);
  }, [delayMs]);
  if (!show) return null;
  return <PageSkeleton />;
}
